import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getApiBaseUrl } from '../lib/api';
import { getAuthToken } from '../lib/supabase';
import type { Project } from '../types';

interface TaskFrameProps {
  project: Project;
}

const TaskFrame: React.FC<TaskFrameProps> = ({ project }) => {
  const API_URL = getApiBaseUrl();

  const [isStarting, setIsStarting] = useState(false);
  const [taskingStarted, setTaskingStarted] = useState(false);
  const [iframeFiles, setIframeFiles] = useState<Record<string, string> | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [taskActionDone, setTaskActionDone] = useState<null | { type: 'task-submitted' | 'task-sent-back'; taskId?: string }>(null);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [noTasks, setNoTasks] = useState<boolean | null>(null);
  const [checkingNext, setCheckingNext] = useState<boolean>(false);
  const [nextAvailable, setNextAvailable] = useState<boolean | null>(null);

  // Listen for messages from iframe about submit / send-back / no-tasks
  useEffect(() => {
    if (!taskingStarted) return;
    const handler = (e: MessageEvent) => {
      const msg = e?.data;
      if (!msg || typeof msg !== 'object') return;
      const type = (msg as any).type as string | undefined;
      if (type === 'task-submitted' || type === 'task-sent-back') {
        const taskId = (msg as any).taskId as string | undefined;
        setTaskActionDone({ type, taskId });
        // Kick off availability check for next task
        void checkAvailability();
      }
      if (type === 'no-tasks') {
        setNoTasks(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [taskingStarted]);

  const checkAvailability = async (): Promise<boolean | null> => {
    try {
      setNextAvailable(null);
      const token = await getAuthToken();
      if (!token) return null;
      const resp = await fetch(`${API_URL}/api/tasks/available`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ projectId: project.id })
      });
      if (resp.status === 204) {
        setNextAvailable(false);
        return false;
      }
      if (!resp.ok) {
        setNextAvailable(null);
        return null;
      }
      let data: any = null;
      try {
        const ct = resp.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          data = await resp.json();
        }
      } catch {}
      const available = !!(data && data.available !== false);
      setNextAvailable(available);
      return available;
    } catch {
      setNextAvailable(null);
      return null;
    }
  };

  const handleStartTasking = async () => {
    setTasksError(null);
    setIsStarting(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');
      setAuthToken(token);
      // Load latest interface files
      const resp = await fetch(`${API_URL}/api/projects/${project.id}/code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Failed to load task interface');
      const files: Record<string, string> | undefined = data?.files;
      if (!files || !files['index.html']) throw new Error('Interface is not ready for this project');
      setIframeFiles(files);
      setTaskingStarted(true);
      setTaskActionDone(null);
      setIframeKey((k) => k + 1);
    } catch (e: any) {
      setTasksError(e?.message || 'Failed to start tasking');
    } finally {
      setIsStarting(false);
    }
  };

  // Pre-check availability on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setTasksError(null);
        setNoTasks(null); // indicate checking state
        const token = await getAuthToken();
        if (!token) return;
        const resp = await fetch(`${API_URL}/api/tasks/available`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ projectId: project.id })
        });
        if (cancelled) return;
        if (resp.status === 204) {
          setNoTasks(true);
          return;
        }
        if (!resp.ok) return;
        let availableResult: any = null;
        try {
          const ct = resp.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            availableResult = await resp.json();
          }
        } catch {}
        if (!availableResult) {
          setNoTasks(true);
          return;
        }
        setNoTasks(availableResult.available === false ? true : false);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [project.id]);

  const getNextTask = async () => {
    setTaskActionDone(null);
    setTasksError(null);
    setCheckingNext(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');
      const resp = await fetch(`${API_URL}/api/tasks/available`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ projectId: project.id })
      });
      if (resp.status === 204) {
        setTaskingStarted(false);
        setNoTasks(true);
        return;
      }
      if (!resp.ok) {
        // If check fails, fall back to trying to load next
        setIframeKey((k) => k + 1);
        return;
      }
      let data: any = null;
      try {
        const ct = resp.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          data = await resp.json();
        }
      } catch {}
      if (!data || data.available === false) {
        setTaskingStarted(false);
        setNoTasks(true);
        return;
      }
      // Available
      setNoTasks(false);
      setIframeKey((k) => k + 1);
    } catch (e: any) {
      setTasksError(e?.message || 'Failed to check for next task');
      // fallback to attempt
      setIframeKey((k) => k + 1);
    } finally {
      setCheckingNext(false);
    }
  };

  const srcDoc = useMemo(() => {
    if (!iframeFiles) return '';
    const supabaseUrl = (import.meta as any).env.VITE_SUPABASE_URL || '';
    const supabaseAnonKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY || '';
    const html = iframeFiles['index.html'] || '<!doctype html><html><head></head><body><pre>No interface</pre></body></html>';
    const cfg = {
      VITE_SUPABASE_URL: supabaseUrl,
      VITE_SUPABASE_ANON_KEY: supabaseAnonKey,
      PREVIEW_MODE: false,
      PROJECT_ID: project.id,
      API_URL: API_URL,
      AUTH_TOKEN: authToken || ''
    } as const;
    const configScript = `<script>window.__CONFIG__=${JSON.stringify(cfg)};<\/script>`;
    const fetchWrapper = `
<script>(function(){
  try {
    var cfg = window.__CONFIG__ || {};
    var origFetch = window.fetch;
    window.fetch = async function(input, init){
      try {
        var url = (typeof input === 'string') ? input : (input && input.url);
        var method = (init && init.method) ? String(init.method).toUpperCase() : 'GET';
        if (typeof url === 'string' && url.indexOf('/api/') === 0) {
          url = (cfg.API_URL || '') + url;
          input = url;
        }
        var headers = (init && init.headers) ? (init.headers instanceof Headers ? init.headers : new Headers(init.headers)) : new Headers();
        if (cfg.AUTH_TOKEN && !headers.has('Authorization')) {
          headers.set('Authorization', 'Bearer ' + cfg.AUTH_TOKEN);
        }
        if (typeof url === 'string' && url.indexOf('/api/tasks/get') !== -1 && method === 'POST') {
          var bodyObj = {};
          try { bodyObj = init && init.body ? JSON.parse(init.body) : {}; } catch(e){}
          if (cfg.PROJECT_ID && !bodyObj.projectId) bodyObj.projectId = cfg.PROJECT_ID;
          init = Object.assign({}, init, { headers: headers, body: JSON.stringify(bodyObj) });
        } else {
          init = Object.assign({}, init, { headers: headers });
        }
        var resp = await origFetch(input, init);
        try {
          var u = (typeof url === 'string') ? url : '';
          if (resp && resp.ok) {
            if (u.indexOf('/api/tasks/submit') !== -1 && (method === 'POST' || method === 'PUT')) {
              var body = undefined;
              try { body = init && init.body ? JSON.parse(init.body) : undefined; } catch(e){}
              window.parent && window.parent.postMessage({ type: 'task-submitted', taskId: body && body.taskId }, '*');
            }
            if (u.indexOf('/api/tasks/send-back') !== -1 && (method === 'POST' || method === 'PUT')) {
              var body2 = undefined;
              try { body2 = init && init.body ? JSON.parse(init.body) : undefined; } catch(e){}
              window.parent && window.parent.postMessage({ type: 'task-sent-back', taskId: body2 && body2.taskId }, '*');
            }
            if (u.indexOf('/api/tasks/get') !== -1 && method === 'POST') {
              try {
                if (resp.status === 204) {
                  window.parent && window.parent.postMessage({ type: 'no-tasks' }, '*');
                } else {
                  var clone = resp.clone();
                  clone.json().then(function(data){
                    if (!data || !data.taskId) {
                      window.parent && window.parent.postMessage({ type: 'no-tasks' }, '*');
                    }
                  }).catch(function(){
                    window.parent && window.parent.postMessage({ type: 'no-tasks' }, '*');
                  });
                }
              } catch(e){}
            }
          }
        } catch(e) {}
        return resp;
      } catch(e) {
        return origFetch(input, init);
      }
    };
  } catch(e){}
})();<\/script>`;
    return html
      .replace('%SUPABASE_URL%', supabaseUrl)
      .replace('%SUPABASE_ANON_KEY%', supabaseAnonKey)
      .replace('</head>', `${configScript}${fetchWrapper}</head>`);
  }, [iframeFiles, API_URL, project.id, authToken]);

  if (!taskingStarted) {
    if (noTasks === null) {
      return (
        <div className="py-1 text-slate-600 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking task availability…
        </div>
      );
    }
    if (noTasks) {
      return (
        <div>
          <p className="text-slate-700">All tasks are completed.</p>
        </div>
      );
    }
    return (
      <div>
        <p className="text-slate-600 mb-4">When you are ready, start tasking to claim a task and open the task interface.</p>
        {tasksError && (
          <div className="mb-3 text-sm text-red-600">{tasksError}</div>
        )}
        <button
          onClick={handleStartTasking}
          disabled={isStarting}
          className="px-4 py-2 text-white rounded-full disabled:bg-slate-300"
          style={{ backgroundColor: 'var(--accent)' }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
        >
          {isStarting ? 'Starting…' : 'Start tasking'}
        </button>
      </div>
    );
  }

  if (taskActionDone) {
    return (
      <div>
        <div className="mb-4 text-slate-700">
          {taskActionDone.type === 'task-submitted' ? 'Task submitted.' : 'Task sent back to queue.'}
        </div>
        {nextAvailable === null ? (
          <div className="inline-flex items-center gap-2 text-slate-600">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking next task…
          </div>
        ) : nextAvailable === false ? (
          <div className="text-slate-700">All tasks are completed.</div>
        ) : (
          <button
            onClick={getNextTask}
            className="px-4 py-2 text-white rounded-full"
            style={{ backgroundColor: 'var(--accent)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
          >
            Get next task
          </button>
        )}
      </div>
    );
  }

  // Full-screen iframe for active task
  return (
    <div className="fixed inset-0 z-50 bg-white">
      {checkingNext ? (
        <div className="h-full w-full flex items-center justify-center text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking next task…
        </div>
      ) : iframeFiles && authToken ? (
        <iframe
          key={iframeKey}
          title="Task Interface"
          style={{ width: '100%', height: '100%', border: 0 }}
          srcDoc={srcDoc}
        />
      ) : (
        <div className="h-full w-full flex items-center justify-center text-slate-600">Preparing task interface…</div>
      )}
    </div>
  );
};

export default TaskFrame;


